//
// ChatPic - Component for displaying image messages with zoom and save functionality
// Handles picture display in chat with overlay zoom, download capability, and notes
//

import PropTypes from 'prop-types';
import {
  useState,
  useEffect,
  memo,
  useMemo,
  useRef,
} from 'react';

import { useDisplay } from '../../../../../stores';
import { taskStepLabel } from '../../../../../utils';
import { Button } from '../../../../buttons';
import {
  ImagePanAndZoom,
  Row,
} from '../../../../display';
import { useIsMounted } from '../../../../React';
import SvgColorImg from '../../../../SvgColorImg';

import MessageText from './MessageText';

const areEqual = (prev, next) => {
  let diff;
  if ((!diff) && (prev.csid !== next.csid)) diff = 'csid';
  if ((!diff) && (prev.msgId !== next.msgId)) diff = 'msgId';
  if ((!diff) && (prev.qstnId !== next.qstnId)) diff = 'qstnId';

  if (diff !== undefined) {
    console.log(`ChatPic refresh src=${diff}`);
    return false;
  }

  return true;
};

const ChatPic = memo(
  (props) => {
    const {
      translate,
      message,
      getMedia,
      csid,
      piid,
      refreshCellSize,
    } = props;
    const {
      qstnId,
      note,
      qstnTxt,
      unknown,
      task,
      // fileInfo,
      msgId,
    } = message;
    // A picture reporting a task step is that step's proof, not a picture
    // somebody happened to add: the chat has to say which it is looking at
    const stepReported = task?.step?.type;
    const [pic, setPic] = useState((csid)
      ? getMedia({
          csid, piid, msgId, type: 'img',
        })
      : null);
    const isMountedRef = useIsMounted();
    const { display } = useDisplay();
    const {
      browserHeight,
      browserWidth,
      setOverlay,
    } = display;

    const picStyle = useMemo(
      () => {
        const style = {
          maxHeight: browserHeight * 0.75,
          maxWidth: browserWidth * 0.75,
        };

        return style;
      },
      [browserHeight, browserWidth],
    );

    useEffect(
      () => {
        if ((!pic) && csid) {
          getMedia(
            {
              csid,
              piid,
              msgId,
              type: 'img',
            },
            (err, newPic) => {
              if ((!err) && isMountedRef.current) {
                // console.log(`Got media for msgId=${msgId}`);
                setPic(newPic);
              }
            },
          );
        }
      },
      [pic, csid, piid, getMedia, msgId, isMountedRef],
    );

    // Re-measuring re-renders the row, which reloads the picture on some browsers: measure once
    const sizedRef = useRef(false);
    const onPicLoad = () => {
      if (sizedRef.current) return;
      sizedRef.current = true;
      refreshCellSize();
    };

    const savePic = () => {
      // Create a temporary <a> element
      const link = document.createElement('a');
      link.href = pic;

      // Setting the download attribute gives a suggested filename
      link.setAttribute(
        'download',
        `paxity_${msgId}`,
      );

      // Append it to the body (required for the click to work in some browsers)
      document.body.appendChild(link);

      // Trigger the download
      link.click();

      // Clean up
      document.body.removeChild(link);
    };

    const onClick = () => {
      setOverlay(
        <ImagePanAndZoom
          className="z-2000 backdrop-blur-sm w-screen fixed top-0 left-0 h-full bg-black/60 py-2"
          style={{ height: `${browserHeight}px` }}
          onClick={() => { setOverlay(); }}
          src={pic}
        >

          <Button
            imgSrc="/images/svg/gui/cross.svg"
            noFrame
            imgColor="#FFFFFF"
            // tiny
            absolute
            className="top-0 right-0 fill-white mix-blend-difference"
            onClick={() => { setOverlay(); }}
            imgColorAsCssFill
            small={false}
          />

          <Row absolute fullw className="absolute bottom-0 pb-5">
            <Button
              label={translate('save')}
              onClick={savePic}
              baseColor
            />
          </Row>

        </ImagePanAndZoom>,
      );
    };

    // Remove overlay when unmounted:
    useEffect(
      () => () => { setOverlay(); },
      [setOverlay],
    );

    if (unknown) {
      const unknownMessage = `${translate(qstnId, 'questionnaireTitles')}${(qstnTxt) ? ` ${qstnTxt}: ` : ': '}${translate('impossible')}`;

      return (
        <div className="w-full">
          <MessageText
            message={unknownMessage}
            author={null}
            italic
            bold
          />
        </div>
      );
    }

    return (
      <div className="w-full">
        {(stepReported)
          ? (
              <Row start gap={1} className="pb-1">
                <div className="h-5 w-5 shrink-0 rounded-full bg-white p-0.5">
                  <SvgColorImg
                    src="/images/svg/gui/crossHair.svg"
                    className="h-full w-full fill-pxTaskMinePicto"
                    imgColorAsCssFill
                  />
                </div>
                <span className="text-sm font-semibold">
                  {taskStepLabel(translate, stepReported)}
                </span>
              </Row>
            )
          : null}

        {(qstnId)
          ? (
              <MessageText
                message={`${translate(qstnId, 'questionnaireTitles')}${(qstnTxt) ? ` ${qstnTxt}:` : ':'}`}
                author={null}
                italic
                bold
              />
            )
          : null}

        <Row
          className="overflow-hidden border border-pxOutlineStrong/30 rounded"
          onClick={onClick}
        >
          <img
            className="object-contain m-auto w-full h-full max-h-[400px] max-w-[400px]"
            alt=""
            src={pic}
            style={picStyle}
            // Update the message size in cache once the pic has loaded
            onLoad={onPicLoad}
          />
        </Row>

        {(note)
          ? (
              <div className="w-full">
                <MessageText
                  message={(
                    <>
                      <u className="text-sm">{`${translate('notes')}:`}</u>
                      <br />
                      <span className="ml-2.5">{note}</span>
                    </>
                  )}
                  author={null}
                />
              </div>
            )
          : null}

      </div>
    );
  },
  areEqual,
);
ChatPic.displayName = 'ChatPic';

ChatPic.propTypes = {
  translate: PropTypes.func.isRequired,
  message: PropTypes.shape({
    id: PropTypes.number.isRequired,
    msgId: PropTypes.number.isRequired,
    qstnId: PropTypes.string,
    qstnTxt: PropTypes.string,
    note: PropTypes.string,
    unknown: PropTypes.bool,
    task: PropTypes.shape({
      step: PropTypes.shape({
        type: PropTypes.string,
      }),
    }),
    fileInfo: PropTypes.shape({
      height: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      // durationTS: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  getMedia: PropTypes.func.isRequired,
  csid: PropTypes.string.isRequired,
  piid: PropTypes.string,
  refreshCellSize: PropTypes.func.isRequired,
};

export default ChatPic;
